'use client'

import { useEffect } from 'react'
import { recoverFromBoundaryError } from '@/lib/errors/error-boundary-recovery'
import { useErrorPageCopy } from './use-error-page-copy'

/**
 * Route-segment error boundary, re-exported as the `error.tsx` of every root
 * layout (see `RootDocument`). Forwards the thrown error into Datadog
 * RUM (when initialized) and renders a neutral fallback with a `Try
 * again` button that calls `reset()` to re-render the segment.
 *
 * A stale-deploy `ChunkLoadError` is recovered with one hard reload
 * instead, because `reset()` would only re-request the missing chunk.
 *
 * Kept design-system-light so it works inside any segment of the app; its
 * copy is in the reader's language wherever a catalog reaches it
 * (`useErrorPageCopy`).
 */
export default function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const t = useErrorPageCopy()
  useEffect(() => {
    recoverFromBoundaryError(error)
  }, [error])

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
      <h2 className="font-semibold text-xl">{t('title')}</h2>
      <p className="text-muted-foreground text-sm">{t('description')}</p>
      <button
        type="button"
        onClick={reset}
        className="rounded-lg border px-4 py-2 font-medium text-sm hover:bg-muted"
      >
        {t('retry')}
      </button>
    </div>
  )
}
